import { FormGroup } from "components/UIComponents/FormGroup";
import { SecondaryButton } from "components/UIComponents/SecondaryButton";
import { Slider } from "components/UIComponents/Slider";
import { SliderToggle } from "components/UIComponents/SliderToggle";
import { SubTitle } from "components/UIComponents/SubTitle";
import { EditorGroup } from "features/editor/sidebar/EditorGroup";
import { assoc } from "ramda";
import React from "react";
import { CSSProperties, ReactNode } from "react";
import { FormattedMessage } from "react-intl";
import { ControlLine } from "./controls/ControlLine";
import { LabelColorPicker } from "./controls/LabelColorPicker";
import { LabelFontFamilyPicker } from "./controls/LabelFontFamilyPicker";
import { LabelImagePicker } from "./controls/LabelImagePicker";
import { ToggleLine } from "./controls/ToggleLine";
import { Variation, ContainerNode, CSSStyle, LabelDocument, LabelTemplate, LogoNode, TemplateData, TemplateNode, TemplateNodeId, TextNode, LabelItem } from "./LabelDocumentModel";
import { findNode } from "./labelEditorUtils";
import { getNumberFromString } from "./utils";


export interface PreviewRenderProps {
    active?: boolean;
    onNodeClick?: (nodeId: TemplateNodeId) => void;
    activeNodeId?: TemplateNodeId;
}

export interface EditorRenderProps {
    onNodeUpdated: (node: TemplateNode)=>void;
    onNodeReset: (nodeId: TemplateNodeId) => void;
    onVariationApplied: (variation: Variation)=>void;
    onTemplateReset: ()=>void;
    onBackgroundReset: ()=>void;
}


export class LabelDocumentRenderer {
    // key = node type, value = node renderer class
    protected renderersMap: Record<string, new (...args: any[]) => LabelNodeRenderer>;
    protected labelTemplate: LabelTemplate;
    protected templateData: TemplateData;
    constructor(
        protected labelDocument: LabelDocument,
        protected labelItem: LabelItem
    ) {
        this.renderersMap = {
            "container": ContainerNodeRenderer,
            "text": TextNodeRenderer,
            "logo": LogoNodeRenderer
        }
        this.labelTemplate = this.labelDocument.template;
        this.templateData = this.labelItem.templateData;
    }

    public renderTemplate(renderProps: PreviewRenderProps): React.ReactNode[] {
        return this.renderNodes(
            this.templateData.document,
            renderProps
        );
    }

    public renderNodes(templateNodes: TemplateNode[], renderProps: PreviewRenderProps): React.ReactNode[] {
        const nodes: React.ReactNode[] = [];
        for (const node of templateNodes) {
            const renderer = this.createRenderer(node, renderProps);
            nodes.push(renderer.renderTemplate());
        }
        return nodes;
    }

    public renderEditor(activeNodeId: string, renderProps: PreviewRenderProps, editorProps: EditorRenderProps): ReactNode|null {
        const node = findNode(this.templateData.document, node=>node.id===activeNodeId);
        if (!node) return null;
        const renderer = this.createRenderer(node, renderProps, editorProps);
        return renderer.renderEditor();
    }

    protected createRenderer(
        node: TemplateNode, 
        previewRenderProps: PreviewRenderProps,
        editorRenderProps?: EditorRenderProps
    ): LabelNodeRenderer {
        const rendererClass = this.renderersMap[node.type];
        if (!rendererClass) throw new Error(`Cannot find renderer of type ${node.type}`);
        return new rendererClass(this, node, previewRenderProps, editorRenderProps);
    }

    public getLabelTemplate(): Readonly<LabelTemplate> {
        return this.labelTemplate;
    }

    public getLabelTemplateData(): Readonly<TemplateData> {
        return this.templateData;
    }

    public getLabelItem(): Readonly<LabelItem> {
        return this.labelItem;
    }
}


abstract class LabelNodeRenderer<T extends TemplateNode = TemplateNode> {
    constructor(
        protected docRenderer: LabelDocumentRenderer,
        protected node: T,
        protected previewRenderProps: PreviewRenderProps,
        protected editorRenderProps?: EditorRenderProps
    ) {}
    abstract renderTemplate(): React.ReactNode;
    abstract renderEditor(): React.ReactNode;
    renderEditorResetNode(): React.ReactNode {
        return <div className="mt-2">
            <SecondaryButton onClick={()=>this.editorRenderProps?.onNodeReset(this.node.id)}>
                <FormattedMessage id="editor.label.resetNode" />
            </SecondaryButton>
        </div>
    }
    createWrapperStyle(): CSSStyle {
        let style: CSSStyle = {};
        if (this.node.position) {
            const {x,y} = this.node.position;
            const left = `${x}mm`;
            const top = `${y}mm`;
            style.transform = `translate(${left}, ${top})`;
        }
        return style;
    }
    createStyle(): CSSStyle {
        const templateData = this.docRenderer.getLabelTemplateData();
        let style = {...templateData.defaultStyle} || {};
        style.display = "inline-block";
        style.position = "absolute";
        style.left = "0";
        style.top = "0";
        if (this.node.position) {
            if (this.node.position.origin === "center") {
                style.transform = "translate(-50%, -50%)"
            }
            if (this.node.position.originX === "center") {
                style.transform = "translate(-50%, 0)"
            }
            if (this.node.position.originY === "center") {
                style.transform = "translate(0, -50%)"
            }
        }
        if (this.node.style) {
            style = {
                ...style,
                ...this.node.style
            }
        }
        if (this.node.hidden) {
            style.display = "none"
        }
        return style;
    }
    createElementProps(): {key: string} & Pick<
        JSX.IntrinsicElements["div"],
        "style"|"className"|"onClick"
    > {
        let className = "";
        if (this.previewRenderProps.active
            && this.node.id === this.previewRenderProps.activeNodeId
        ) {
            className = "outline outline-1 outline-primary";
        }
        return {
            key: this.node.id,
            style: this.createStyle(),
            className,
            onClick: (e) => {
                if (!this.previewRenderProps.active 
                    || !this.previewRenderProps.onNodeClick
                    || (e as any).clickHandled
                ) return;
                (e as any).clickHandled = true;
                this.previewRenderProps.onNodeClick(this.node.id);
            }
        }
    }
    createAbsoluteUrl(url: string) {
        if (!url.startsWith("/")) {
            return url;
        }
        return document.location.origin + url;
    }
    updateNode<K extends Extract<keyof T, String>>(property: K, value: T[K], node?: TemplateNode) {
        node = node || this.node;
        const newNode = assoc(property, value, node) as T;
        
        if (this.editorRenderProps?.onNodeUpdated) {
            this.editorRenderProps.onNodeUpdated(newNode);
        }
    }
    updateNodeStyle<SN extends Extract<keyof S, String>, S=Exclude<T["style"], undefined>>(styleName: SN, styleValue: S[SN], node?: TemplateNode) {
        node = node || this.node;
        const newStyle = assoc(styleName, styleValue, node.style||{}) as CSSStyle;
        
        // todo fix type
        this.updateNode(
            "style" as Extract<keyof T, String>, 
            newStyle as T[Extract<keyof T, String>],
            node
        );
    }
}

class ContainerNodeRenderer extends LabelNodeRenderer<ContainerNode> {
    createStyle(): CSSProperties {
        const style = super.createStyle();
        style.left = "0mm";
        style.top = "0mm";
        style.width = "100%";
        style.height = "100%";
        style.backgroundSize = "100% 100%";
        return style;
    }
    renderTemplate(): ReactNode {
        const wrapperStyle = this.createStyle();
        const imageStyle = this.createStyle();
        
        if (this.node.background?.color) {
            wrapperStyle.backgroundColor = this.node.background?.color;
        }
        if (this.node.background?.url) {
            imageStyle.backgroundImage = `url(${this.createAbsoluteUrl(this.node.background?.url)})`;
        }

        const children = this.docRenderer.renderNodes(
            this.node.children,
            this.previewRenderProps
        );
        return <div {...{...this.createElementProps(), style: wrapperStyle}}>
            <div {...{...this.createElementProps(), style: imageStyle}}>
                {children}
            </div>
        </div>
    }
    renderEditor(): ReactNode {
        const variations = this.docRenderer.getLabelTemplate().variations;

        const onTemplateReset = () => {
            this.editorRenderProps?.onTemplateReset();
        }

        return <div className="flex flex-col">
            <div className="px-6 pt-6">
                <SubTitle><FormattedMessage id="editor.label.variations" /></SubTitle>
                <FormGroup groupTitle={<></>}>
                    <select
                        className="py-3 px-3 w-full rounded border border-gray-1 outline-0"
                        onChange={(e)=>{
                            const variation = variations.find(v=>v.name===e.target.value)
                            this.editorRenderProps?.onVariationApplied(variation!);
                        }}
                        value={this.docRenderer.getLabelItem().variation.name}
                    >
                        {variations.map((v,i) => {
                            return <option value={v.name} key={i}>
                                {v.name}
                            </option>
                        })}
                    </select>
                    <div className="cursor-pointer underline text-xs text-right"
                        onClick={onTemplateReset}
                    >
                        <FormattedMessage id="editor.label.resetTemplate" />
                    </div>
                </FormGroup>
            </div>
            {["text", "logo"]
                .map(t => this.node.children.filter(n=>n.type===t))
                .filter(n => n.length > 0)
                .map((nodes, tI) => {
                    return <React.Fragment key={tI}>
                        <div className="px-6 pt-6">
                            <SubTitle>{nodes[0].type.toUpperCase()}</SubTitle>
                        </div>
                        <EditorGroup>
                            {nodes
                                .map((textNode, i) => {
                                    return <ControlLine 
                                        key={i} 
                                        left={
                                            <div className="flex flex-col">
                                                <div className="cursor-pointer underline" 
                                                    onClick={()=>this.previewRenderProps?.onNodeClick?.(textNode.id)}
                                                >
                                                    {textNode.id}
                                                </div>
                                            </div>
                                        }
                                        right={
                                            <div className="h-7 w-12 flex-none">
                                                <SliderToggle
                                                    enabled={!textNode.hidden}
                                                    setEnabled={e=>this.updateNode('hidden', !e, textNode)}
                                                />
                                            </div>
                                        }
                                    />
                                })
                            }
                        </EditorGroup>
                    </React.Fragment>
            })}
            <div className="pt-6 px-6">
                <SubTitle><FormattedMessage id="editor.label.background" /></SubTitle>
            </div>
            <EditorGroup>
                <ControlLine 
                    left={"Color"}
                    right={<LabelColorPicker
                        color={this.node.background.color||"white"}
                        onChange={color=>{
                            this.updateNode(
                                "background",
                                {
                                    ...this.node.background,
                                    color
                                }
                            )
                        }}
                    />}
                />
                <FormGroup groupTitle={"Image"}>
                    <LabelImagePicker 
                        url={this.node.background?.url||null}
                        aspect={
                            this.docRenderer.getLabelTemplate().size.width/this.docRenderer.getLabelTemplate().size.height
                        }
                        onChange={url=>{
                            this.updateNode(
                                "background",
                                {
                                    ...this.node.background,
                                    url: url||undefined
                                }
                            )
                        }}
                    />
                </FormGroup>
                <div className="mt-2">
                    <SecondaryButton onClick={()=>this.editorRenderProps?.onBackgroundReset()}>
                        <FormattedMessage id="editor.label.resetTemplate" />
                    </SecondaryButton>
                </div>
            </EditorGroup>
        </div>
    }
}


class TextNodeRenderer extends LabelNodeRenderer<TextNode> {
    createStyle(): React.CSSProperties {
        const style = super.createStyle();
        style.lineHeight = style.fontSize;
        style.textAlign = "center";
        return style;
    }
    renderTemplate(): ReactNode {
        let html = this.node.content;
        html = html.replaceAll(" ", "&nbsp;");
        return <div style={this.createWrapperStyle()}>
            <div {...this.createElementProps()}>
                <div style={{whiteSpace: "pre"}} 
                    dangerouslySetInnerHTML={{__html: html}}
                />
            </div>
        </div>
    }
    renderEditor(): ReactNode {
        const fontSize = getNumberFromString(this.node.style?.fontSize?.toString()||"12pt");
        const updateFontSize = (fontSize: number) => {
            this.updateNodeStyle("fontSize", `${fontSize}pt`);
        }

        return <EditorGroup>
            <FormGroup groupTitle={<>Font</>}>
                <LabelFontFamilyPicker
                    font={this.node.style?.fontFamily||""}
                    onChange={ff=>{
                        this.updateNodeStyle("fontFamily", ff)
                    }}
                />
            </FormGroup>
            <ControlLine
                left={"Color"} 
                right={<LabelColorPicker
                    color={this.node.style?.color||"black"}
                    onChange={color=>{
                        this.updateNodeStyle("color", color)
                    }}
                />}
            />
            <FormGroup
                groupTitle="Text"
            >
                <textarea
                    className={`py-3 pl-4 pr-2 w-full text-sm
                    rounded border border-gray-1 outline-0
                    focus:border-focus h-[85px]`}
                    value={this.node.content}
                    onChange={e => this.updateNode('content', e.target.value)}
                />
            </FormGroup>
            <FormGroup groupTitle={<>Font size ({fontSize}pt)</>}>
                <Slider
                    min={10}
                    max={70}
                    step={0.25}
                    value={fontSize}
                    onChange={updateFontSize}
                />
            </FormGroup>
            {([
                ["fontWeight", "bold"],
                ["fontStyle", "italic"],
            ] as string[][]).map(([styleNameNT, value], i) => {
                const styleName = styleNameNT as keyof CSSStyle;
                const currStyle = this.node.style?.[styleName]||undefined;
                const enabled = (currStyle === value);

                return <ToggleLine 
                    key={i}
                    text={<FormattedMessage id={`editor.label.text.${value}`}/>}
                    enabled={enabled}
                    onChange={e=>this.updateNodeStyle(styleName, e?value:undefined)}
                />
            })}
            {this.renderEditorResetNode()}
        </EditorGroup>
    }
}


class LogoNodeRenderer extends LabelNodeRenderer<LogoNode> {
    renderTemplate(): ReactNode {
        const wrapperStyle = this.createStyle();

        const imageWidth = getNumberFromString(this.node.style?.width?.toString()||"30mm");
        const padding = imageWidth/10;
        wrapperStyle.width = `${imageWidth+padding*2}mm`;
        wrapperStyle.padding = `${padding}mm`;

        if (this.node.withBackground&&this.node.logoBackgroundUrl) {
            wrapperStyle.backgroundImage = `url(${this.createAbsoluteUrl(this.node.logoBackgroundUrl)})`;
            wrapperStyle.backgroundSize = `100% 100%`;
        }

        return <div style={this.createWrapperStyle()}>
            <div {...{...this.createElementProps(), style: wrapperStyle}}>
                <div>
                    {this.node.logoUrl&&<img
                        src={this.createAbsoluteUrl(this.node.logoUrl)} 
                        alt={""}
                        style={{
                            width: `${imageWidth}mm`
                        }}
                    />}
                </div>
            </div>
        </div>
    }
    renderEditor(): ReactNode {
        const imageWidth = getNumberFromString(this.node.style?.width?.toString()||"30mm");
        const updateImageWidth = (width: number) => {
            this.updateNodeStyle(
                "width",
                `${width}mm`
            )
        }
        return (
            <div>
                <div className="pt-6 px-6">
                    <SubTitle>Logo</SubTitle>
                </div>
                <EditorGroup>
                    {this.node.logoBackgroundUrl&&
                        <ToggleLine 
                            text={"with background"}
                            enabled={this.node.withBackground||false}
                            onChange={e=>this.updateNode(
                                "withBackground",
                                e
                            )}
                        />
                    }
                    <LabelImagePicker 
                        url={this.node.logoUrl||null}
                        onChange={url=>{
                            this.updateNode(
                                "logoUrl",
                                url||undefined
                            )
                        }}
                    />
                    <FormGroup groupTitle={<>Image width ({imageWidth}mm)</>}>
                        <Slider
                            min={20}
                            max={60}
                            value={imageWidth}
                            onChange={updateImageWidth}
                        />
                    </FormGroup>
                    {this.renderEditorResetNode()}
                </EditorGroup>
            </div>
        )
    }
}
